/**
 * Backport of the Bootstrap 6 datepicker (js/src/datepicker.ts) to Bootstrap 5.
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
 *
 * Differences from upstream: it is standalone (Bootstrap 5 does not expose
 * BaseComponent/EventHandler), written in plain JS, and the data API is
 * delegated on `document` so it works with Turbo navigation.
 */

import { Calendar, months } from 'vanilla-calendar-pro';

const NAME = 'datepicker';
const EVENT_KEY = `.bs.${NAME}`;

const EVENT_CHANGE = `change${EVENT_KEY}`;
const EVENT_SHOW = `show${EVENT_KEY}`;
const EVENT_SHOWN = `shown${EVENT_KEY}`;
const EVENT_HIDE = `hide${EVENT_KEY}`;
const EVENT_HIDDEN = `hidden${EVENT_KEY}`;

const SELECTOR_DATA_TOGGLE = '[data-bs-toggle="datepicker"]';

const HIDE_DELAY = 100; // ms delay before hiding after selection

const Default = {
  datepickerTheme: null, // 'light', 'dark', 'auto' - explicit theme for the popover only
  dateMin: null,
  dateMax: null,
  boundInput: null, // Element/selector of a hidden input receiving ISO date(s), for form submission
  boundEndInput: null, // With boundInput: receives the last date, boundInput only the first
  dateFormat: null, // Intl.DateTimeFormat options, or function(date, locale) => string
  displayElement: null, // Element to show formatted date (defaults to element for buttons)
  displayMonthsCount: 1, // Number of months to display side-by-side
  firstWeekday: 1, // Monday
  inline: false, // Render calendar inline (no popup)
  locale: 'default',
  positionElement: null, // Element to position calendar relative to (defaults to input)
  selectedDates: [],
  selectionMode: 'single', // 'single', 'multiple', 'multiple-ranged'
  placement: 'left', // 'left', 'center', 'right', 'auto'
  todayButton: true, // Show a button that jumps to today (and selects it in single mode)
  todayLabel: null, // Button text, defaults to I18n.t("dates.today")
  vcpOptions: {} // Pass-through for any Vanilla Calendar Pro option
};

const instances = new WeakMap();

const trigger = (element, name, detail = {}) => {
  const event = new Event(name, { bubbles: true, cancelable: true });
  Object.assign(event, detail);
  element.dispatchEvent(event);
  return event;
};

const isDisabled = element =>
  element.disabled === true || element.classList.contains('disabled') || element.hasAttribute('disabled');

const normalizeData = value => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value === 'null' || value === '') return null;
  if (value === String(Number(value))) return Number(value);

  if (/^[[{]/.test(value)) {
    try {
      return JSON.parse(decodeURIComponent(value));
    } catch {
      return value;
    }
  }

  return value;
};

// Reads `data-bs-*` attributes, e.g. `data-bs-selection-mode="multiple-ranged"`
const getDataAttributes = element => {
  const attributes = {};

  for (const key of Object.keys(element.dataset)) {
    if (!key.startsWith('bs') || key === 'bs' || key === 'bsToggle') continue;

    const name = key.slice(2).charAt(0).toLowerCase() + key.slice(3);
    attributes[name] = normalizeData(element.dataset[key]);
  }

  // Selected dates may be a JSON array or a comma separated list
  if (typeof attributes.selectedDates === 'string') {
    attributes.selectedDates = attributes.selectedDates.split(',').map(date => date.trim());
  }

  return attributes;
};

class Datepicker {
  constructor(element, config = {}) {
    this._element = typeof element === 'string' ? document.querySelector(element) : element;

    if (!this._element) return;

    this._config = {
      ...Default,
      ...getDataAttributes(this._element),
      ...(typeof config === 'object' ? config : {})
    };

    // Allow a single date to be given as a plain string
    if (typeof this._config.selectedDates === 'string') {
      this._config.selectedDates = [this._config.selectedDates];
    }

    instances.set(this._element, this);

    this._calendar = null;
    this._isShown = false;

    this._initCalendar();
  }

  static get Default() {
    return Default;
  }

  static get NAME() {
    return NAME;
  }

  static getInstance(element) {
    return instances.get(element) || null;
  }

  static getOrCreateInstance(element, config = {}) {
    return Datepicker.getInstance(element) || new Datepicker(element, typeof config === 'object' ? config : {});
  }

  toggle() {
    if (this._config.inline) return;

    return this._isShown ? this.hide() : this.show();
  }

  show() {
    if (this._config.inline) return;

    if (!this._calendar || isDisabled(this._element) || this._isShown) return;

    if (trigger(this._element, EVENT_SHOW).defaultPrevented) return;

    this._calendar.show();
    this._isShown = true;

    trigger(this._element, EVENT_SHOWN);
  }

  hide() {
    if (this._config.inline) return;

    if (!this._calendar || !this._isShown) return;

    if (trigger(this._element, EVENT_HIDE).defaultPrevented) return;

    this._calendar.hide();
    this._isShown = false;

    trigger(this._element, EVENT_HIDDEN);
  }

  dispose() {
    if (this._themeObserver) {
      this._themeObserver.disconnect();
      this._themeObserver = null;
    }

    if (this._onOutside) {
      document.removeEventListener('focusin', this._onOutside);
      document.removeEventListener('pointerdown', this._onOutside);
    }

    if (this._onInput) {
      this._element.removeEventListener('input', this._onInput);
      this._element.removeEventListener('change', this._onChange);
    }

    if (this._onTriggerClick) {
      this._positionElement.removeEventListener('click', this._onTriggerClick);
    }

    if (this._calendar) {
      this._calendar.destroy();
    }

    this._calendar = null;
    instances.delete(this._element);
  }

  getSelectedDates() {
    // Input pickers have no calendar context until first shown, so fall back to the config
    const dates = this._calendar?.context?.selectedDates ?? this._config.selectedDates;
    return dates ? [...dates] : [];
  }

  setSelectedDates(dates) {
    if (this._calendar) {
      this._calendar.set({ selectedDates: dates });
      this._syncSelectedDates([...this._calendar.context.selectedDates]);
    }
  }

  _initCalendar() {
    this._isInput = this._element.tagName === 'INPUT';
    this._isInline = this._config.inline;
    this._boundInput = this._resolveElement(this._config.boundInput);
    this._boundEndInput = this._resolveElement(this._config.boundEndInput);

    // For inline mode, look for a hidden input child to bind to
    if (!this._boundInput && this._isInline && !this._isInput) {
      this._boundInput = this._element.querySelector('input[type="hidden"], input[name]');
    }

    this._positionElement = this._resolvePositionElement();
    this._displayElement = this._resolveDisplayElement();

    // The calendar lives on the position element (for correct popup positioning)
    // but value updates still go to this._element (the input)
    this._calendar = new Calendar(this._positionElement, this._buildCalendarOptions());
    this._calendar.init();

    this._setupThemeObserver();
    this._setupDismiss();
    this._setupManualEdit();

    if (this._isInput && this._element.value) {
      this._parseInputValue();
    }

    this._updateDisplayWithSelectedDates();
  }

  _updateDisplayWithSelectedDates() {
    const calendarDates = [...(this._calendar?.context.selectedDates || [])];
    const selectedDates = calendarDates.length > 0 ? calendarDates : this._config.selectedDates;

    if (selectedDates.length > 0) {
      this._syncSelectedDates(selectedDates);
    }
  }

  _syncSelectedDates(selectedDates, { updateInput = true } = {}) {
    const formattedDate = this._formatDateForInput(selectedDates);

    if (this._isInput && updateInput) {
      this._element.value = formattedDate;
    }

    if (this._boundInput) {
      this._boundInput.value = this._boundEndInput ? (selectedDates[0] ?? '') : selectedDates.join(',');
    }

    if (this._boundEndInput) {
      this._boundEndInput.value = selectedDates.at(-1) ?? '';
    }

    if (this._displayElement) {
      this._displayElement.textContent = formattedDate;
    }
  }

  _resolveElement(elementOrSelector) {
    return typeof elementOrSelector === 'string' ? document.querySelector(elementOrSelector) : elementOrSelector;
  }

  // Typing into the text field updates the calendar and the bound (submitted)
  // inputs. While typing only complete dates (4 digit year) are applied and the
  // text is left alone; on change (blur/enter) the text is normalised to the
  // display format, or restored when it cannot be parsed.
  _setupManualEdit() {
    if (!this._isInput) return;

    this._onInput = () => this._applyTypedValue({ final: false });
    this._onChange = () => this._applyTypedValue({ final: true });

    this._element.addEventListener('input', this._onInput);
    this._element.addEventListener('change', this._onChange);
  }

  _applyTypedValue({ final }) {
    const text = this._element.value.trim();
    const dates = text === '' ? [] : this._parseTypedValue(text, { strict: !final });

    if (!dates) {
      if (final) this._syncSelectedDates(this.getSelectedDates());
      return;
    }

    const current = this.getSelectedDates();
    const unchanged = dates.length === current.length && dates.every((date, index) => date === current[index]);

    if (!unchanged) {
      this._setCalendarDates(dates);
      trigger(this._element, EVENT_CHANGE, { dates, event: null });
    }

    this._syncSelectedDates(dates, { updateInput: final });
  }

  _setCalendarDates(dates) {
    const options = { selectedDates: dates };

    if (dates.length > 0) {
      const first = this._parseDate(dates[0]);
      options.selectedMonth = first.getMonth();
      options.selectedYear = first.getFullYear();
    }

    this._calendar.set(options);
  }

  _parseTypedValue(text, { strict }) {
    const { selectionMode } = this._config;
    let parts = [text];

    if (selectionMode === 'multiple-ranged') {
      parts = text.split(/\s+[–—-]\s+/);
    } else if (selectionMode === 'multiple') {
      parts = text.split(/\s*,\s*/);
    }

    const dates = parts.map(part => this._parseDateText(part, { strict }));
    if (dates.includes(null)) return null;

    dates.sort();

    if (selectionMode === 'single' && dates.length !== 1) return null;

    if (selectionMode === 'multiple-ranged') {
      if (dates.length === 1) dates.push(dates[0]);
      if (dates.length !== 2) return null;
    }

    return dates.every(date => this._isWithinLimits(date)) ? dates : null;
  }

  _isWithinLimits(isoDate) {
    const toIso = value => (value instanceof Date ? this._toIso(value) : String(value).slice(0, 10));
    const { dateMin, dateMax } = this._config;

    return !((dateMin && isoDate < toIso(dateMin)) || (dateMax && isoDate > toIso(dateMax)));
  }

  _toIso(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  // The order of day, month and year follows the display locale, e.g. d-m-y for nl, m/d/y for en-US
  _dateFieldOrder() {
    if (!this._fieldOrder) {
      const locale = this._config.locale === 'default' ? undefined : this._config.locale;
      const { dateFormat } = this._config;
      const options = dateFormat && typeof dateFormat === 'object' ? dateFormat : undefined;

      this._fieldOrder = new Intl.DateTimeFormat(locale, options)
        .formatToParts(new Date(2006, 10, 22))
        .map(({ type }) => type)
        .filter(type => ['day', 'month', 'year'].includes(type));
    }

    return this._fieldOrder;
  }

  // Parses ISO dates and numeric dates in the display locale. Returns an ISO date string or null.
  _parseDateText(text, { strict }) {
    const value = text.trim();
    const fields = {};
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value);

    if (iso) {
      [fields.year, fields.month, fields.day] = iso.slice(1).map(Number);
    } else {
      const match = /^(\d+)\D+(\d+)\D+(\d+)\D*$/.exec(value);
      const order = this._dateFieldOrder();
      if (!match || order.length !== 3) return null;

      for (const [index, type] of order.entries()) fields[type] = Number(match[index + 1]);

      const yearText = match[order.indexOf('year') + 1];
      if (strict && yearText.length !== 4) return null;

      if (yearText.length <= 2) fields.year += 2000;
    }

    const date = new Date(fields.year, fields.month - 1, fields.day);
    const valid =
      date.getFullYear() === fields.year && date.getMonth() === fields.month - 1 && date.getDate() === fields.day;

    return valid ? this._toIso(date) : null;
  }

  _resolvePositionElement() {
    let { positionElement } = this._config;

    if (typeof positionElement === 'string') {
      positionElement = document.querySelector(positionElement);
    }

    // Use the parent of an input if it is in a Bootstrap input group
    if (!positionElement && this._isInput && !this._isInline) {
      positionElement = this._element.closest('.form-adorn, .input-group');
    }

    return positionElement || this._element;
  }

  _resolveDisplayElement() {
    const { displayElement } = this._config;

    if (typeof displayElement === 'string') {
      return document.querySelector(displayElement);
    }

    // For buttons/non-inputs (not inline), look for a [data-bs-datepicker-display] child
    if (displayElement === true || (displayElement === null && !this._isInput && !this._isInline)) {
      return this._element.querySelector('[data-bs-datepicker-display]') || this._element;
    }

    return displayElement;
  }

  _getThemeAncestor() {
    // Cache on first lookup: for an inline calendar, `_syncThemeAttribute` writes
    // `data-bs-theme` onto this same element, which would make a later `closest()`
    // match itself instead of the real ancestor.
    if (this._themeAncestor === undefined) {
      this._themeAncestor = this._element.closest('[data-bs-theme]');
    }

    return this._themeAncestor;
  }

  _getEffectiveTheme() {
    // Priority: explicit datepickerTheme config > inherited from ancestor > none
    const { datepickerTheme } = this._config;
    if (datepickerTheme) return datepickerTheme;

    return this._getThemeAncestor()?.getAttribute('data-bs-theme') || null;
  }

  _syncThemeAttribute(element) {
    if (!element) return;

    const theme = this._getEffectiveTheme();

    if (theme) {
      // VCP appends the popover to body, which breaks CSS theme inheritance
      element.setAttribute('data-bs-theme', theme);
    } else {
      element.removeAttribute('data-bs-theme');
    }
  }

  _setupThemeObserver() {
    const ancestor = this._getThemeAncestor();
    if (!ancestor || this._config.datepickerTheme) return;

    this._themeObserver = new MutationObserver(() => {
      this._syncThemeAttribute(this._calendar?.context?.mainElement);
    });

    this._themeObserver.observe(ancestor, {
      attributes: true,
      attributeFilter: ['data-bs-theme']
    });
  }

  // VCP hides on outside `click` itself, but skips our hide events and ignores
  // focus moves. `focusin` covers keyboard moves, `pointerdown` covers presses on
  // non-focusable elements and is read before the calendar re-renders on click.
  _setupDismiss() {
    if (this._isInline) return;

    // VCP schedules a show on every click of its trigger, after the data API
    // toggle runs. When that toggle closed the popup, cancel the reopen.
    if (!this._isInput) {
      this._onTriggerClick = () => {
        if (!this._isShown) {
          this._calendar?.hide();
        }
      };

      this._positionElement.addEventListener('click', this._onTriggerClick);
    }

    this._onOutside = ({ target }) => {
      const mainElement = this._calendar?.context?.mainElement;
      const isOutside =
        !(target instanceof Node) || (!this._element.contains(target) && !mainElement?.contains(target));

      if (this._isShown && isOutside) {
        this.hide();
      }
    };

    document.addEventListener('focusin', this._onOutside);
    document.addEventListener('pointerdown', this._onOutside);
  }

  _buildCalendarOptions() {
    const theme = this._getEffectiveTheme();
    // VCP uses 'system' for auto, Bootstrap uses 'auto'
    const vcpTheme = !theme || theme === 'auto' ? 'system' : theme;

    const calendarOptions = {
      ...this._config.vcpOptions,
      extensions: [months],
      inputMode: !this._isInline,
      positionToInput: this._config.placement,
      firstWeekday: this._config.firstWeekday,
      locale: this._config.locale,
      selectionDatesMode: this._config.selectionMode,
      selectedDates: this._config.selectedDates,
      displayMonthsCount: this._config.displayMonthsCount,
      type: this._config.displayMonthsCount > 1 ? 'multiple' : 'default',
      selectedTheme: vcpTheme,
      themeAttrDetect: '[data-bs-theme]',
      onClickDate: (self, event) => this._handleDateClick(self, event),
      onInit: self => {
        this._syncThemeAttribute(self.context.mainElement);
        this._ensureTodayButton(self.context.mainElement);
      },
      onUpdate: self => {
        this._ensureTodayButton(self.context.mainElement);
      },
      onShow: () => {
        if (!this._calendar) return;

        this._ensureTodayButton(this._calendar.context.mainElement);

        this._isShown = true;
        this._syncThemeAttribute(this._calendar.context.mainElement);
      },
      onHide: () => {
        this._isShown = false;
      }
    };

    // Navigate to the month of the first selected date
    if (this._config.selectedDates.length > 0) {
      const firstDate = this._parseDate(this._config.selectedDates[0]);
      calendarOptions.selectedMonth = firstDate.getMonth();
      calendarOptions.selectedYear = firstDate.getFullYear();
    }

    if (this._config.dateMin) calendarOptions.dateMin = this._config.dateMin;
    if (this._config.dateMax) calendarOptions.dateMax = this._config.dateMax;

    return calendarOptions;
  }

  // VCP re-renders its layout, so the footer is (re)added after init and every update
  _ensureTodayButton(mainElement) {
    if (!this._config.todayButton || !mainElement || mainElement.querySelector('[data-bs-datepicker-footer]')) return;

    const footer = document.createElement('div');
    footer.dataset.bsDatepickerFooter = '';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-sm btn-primary';
    button.dataset.bsDatepickerToday = '';
    button.textContent = this._getTodayLabel();
    button.disabled = !this._isWithinLimits(this._toIso(new Date()));
    button.addEventListener('click', () => this.goToToday());

    footer.append(button);
    mainElement.append(footer);
  }

  _getTodayLabel() {
    if (this._config.todayLabel) return this._config.todayLabel;

    const translated = globalThis.I18n?.t('dates.today', { defaultValue: '' });
    if (translated) return translated;

    // Fallback when the app translations are not loaded
    const locale = this._config.locale === 'default' ? undefined : this._config.locale;
    const label = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(0, 'day');
    return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
  }

  // Shows the month of today. In single selection mode today is also selected.
  goToToday() {
    if (!this._calendar) return;

    const today = new Date();
    const todayIso = this._toIso(today);

    if (!this._isWithinLimits(todayIso)) return;

    if (this._config.selectionMode === 'single') {
      this._setCalendarDates([todayIso]);
      this._syncSelectedDates([todayIso]);
      trigger(this._element, EVENT_CHANGE, { dates: [todayIso], event: null });
      this._maybeHideAfterSelection([todayIso]);
      return;
    }

    this._calendar.set({
      selectedDates: this.getSelectedDates(),
      selectedMonth: today.getMonth(),
      selectedYear: today.getFullYear()
    });
  }

  _handleDateClick(self, event) {
    const selectedDates = [...self.context.selectedDates];
    this._syncSelectedDates(selectedDates);

    trigger(this._element, EVENT_CHANGE, { dates: selectedDates, event });

    this._maybeHideAfterSelection(selectedDates);
  }

  _maybeHideAfterSelection(selectedDates) {
    if (this._isInline) return;

    const { selectionMode } = this._config;
    const shouldHide =
      (selectionMode === 'single' && selectedDates.length > 0) ||
      (selectionMode === 'multiple-ranged' && selectedDates.length >= 2);

    if (shouldHide) {
      setTimeout(() => this.hide(), HIDE_DELAY);
    }
  }

  _parseDate(dateString) {
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day);
  }

  _formatDate(dateString) {
    const date = this._parseDate(dateString);
    const locale = this._config.locale === 'default' ? undefined : this._config.locale;
    const { dateFormat } = this._config;

    if (typeof dateFormat === 'function') {
      return dateFormat(date, locale);
    }

    if (dateFormat && typeof dateFormat === 'object') {
      return new Intl.DateTimeFormat(locale, dateFormat).format(date);
    }

    return date.toLocaleDateString(locale);
  }

  _formatDateForInput(dates) {
    if (dates.length === 0) return '';

    if (dates.length === 1) return this._formatDate(dates[0]);

    // For date ranges, use en-dash; for multiple dates, use comma
    const separator = this._config.selectionMode === 'multiple-ranged' ? ' – ' : ', ';
    return dates.map(date => this._formatDate(date)).join(separator);
  }

  _parseInputValue() {
    const value = this._element.value.trim();
    if (!value) return;

    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const date = dateOnlyMatch
      ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
      : new Date(value);

    if (!Number.isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      this._calendar.set({ selectedDates: [`${year}-${month}-${day}`] });
    }
  }
}

// Data API: click toggles buttons, focus opens inputs, inline pickers are always visible
document.addEventListener('click', event => {
  const element = event.target instanceof Element && event.target.closest(SELECTOR_DATA_TOGGLE);

  if (!element || element.tagName === 'INPUT' || element.dataset.bsInline === 'true') return;

  event.preventDefault();
  Datepicker.getOrCreateInstance(element).toggle();
});

document.addEventListener('focusin', event => {
  const element = event.target instanceof Element && event.target.closest(SELECTOR_DATA_TOGGLE);

  if (!element || element.tagName !== 'INPUT') return;

  Datepicker.getOrCreateInstance(element).show();
});

// Auto-initialize datepickers that need to render on load: inline ones and
// ones with preselected dates (so the value populates without focus)
const initPreRendered = () => {
  const selector = `${SELECTOR_DATA_TOGGLE}[data-bs-inline="true"], ${SELECTOR_DATA_TOGGLE}[data-bs-selected-dates]`;

  for (const element of document.querySelectorAll(selector)) {
    Datepicker.getOrCreateInstance(element);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPreRendered);
} else {
  initPreRendered();
}

document.addEventListener('turbo:load', initPreRendered);

export default Datepicker;
